import ConfirmationPopover from '@app/components/ConfirmationPopover';
import Flex from '@app/components/Flex';
import Heading from '@app/components/Heading';
import { openModal } from '@app/containers/modals/store/events';
import { rootTaskDeliverablesPathLabel } from '@app/containers/pages/Task/consts';
import TaskDeliverableViewerModal, {
    TaskDeliverableViewerModalProps,
} from '@app/containers/pages/Task/modals/TaskDeliverableViewerModal';
import { $task } from '@app/containers/pages/Task/store/states';
import { Endpoints } from '@app/data/consts';
import { Breadcrumb, BreadcrumbProps, Breadcrumbs, Button, Classes, Icon, Intent } from '@blueprintjs/core';
import NonIdealState from '@components/NonIdealState';
import { $authorizedUser, $permissions } from '@containers/store/states';
import { TaskDeliverableResource } from 'dy-frontend-http-repository/lib/modules/TaskDeliverable/resources';
import { TaskDeliverablePermission } from 'dy-frontend-permissions/lib/permission';
import { useStore } from 'effector-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TaskDeliverableListViewListItem from '../../../../components/TaskDeliverableListViewListItem';
import { useApproveDirectory, useApproveTaskDeliverable, useRemoveTaskDeliverable } from '../../hooks';
import { $taskDeliverablesTree } from '../../store/states';

const TaskDeliverablesApproved: React.FC = () => {
    const permissions = useStore($permissions);
    const me = useStore($authorizedUser);

    const navigate = useNavigate();

    const task = useStore($task);
    const taskDeliverablesTree = useStore($taskDeliverablesTree);
    const { approveDirectory } = useApproveDirectory();
    const { removeTaskDeliverable } = useRemoveTaskDeliverable();
    const { approveTaskDeliverable } = useApproveTaskDeliverable();

    const [approvingPath, setApprovingPath] = useState<string | null>(null);
    const [approvingDeliverableId, setApprovingDeliverableId] = useState<ID | null>(null);
    const [removingDeliverableId, setRemovingDeliverableId] = useState<ID | null>(null);

    const [isApproveAllowed, setIsApproveAllowed] = useState(false);
    const [isArchiveAllowed, setIsArchiveAllowed] = useState(false);

    const [isAuthorizedUserParticipant, setIsAuthorizedUserParticipant] = useState(false);

    useEffect(() => {
        if (!task || !me) {
            return;
        }

        setIsAuthorizedUserParticipant(task.participants.find((p) => p.user.id === me.user.id) !== undefined);
    }, [task, me]);

    useEffect(() => {
        if (!task || !me) {
            return;
        }

        // Root permission set
        if (permissions.isRoot.taskDeliverable) {
            setIsApproveAllowed(true);
            setIsArchiveAllowed(true);
            return;
        }

        // Precalculate if current user is assigned to a task
        const isAssigned = task.participants.find((p) => p.user.id === me.user.id) !== undefined;

        // Permission: approve
        setIsApproveAllowed(
            permissions.has(TaskDeliverablePermission.APPROVE) ||
                (isAssigned && permissions.has(TaskDeliverablePermission.APPROVE_MOD_TASK_ASSIGNED))
        );

        // Permission: archive
        setIsArchiveAllowed(
            permissions.has(TaskDeliverablePermission.ARCHIVE) ||
                (isAssigned && permissions.has(TaskDeliverablePermission.ARCHIVE_MOD_TASK_ASSIGNED))
        );
    }, [task, permissions, me]);

    if (!task || !taskDeliverablesTree) {
        return null;
    }

    const handleOpenTaskDeliverableViewerModal = (
        taskDeliverableId: ID,
        taskDeliverables: TaskDeliverableResource[]
    ) => {
        openModal<TaskDeliverableViewerModalProps>({
            ModalComponent: TaskDeliverableViewerModal,
            data: {
                taskId: task.id,
                taskDeliverableId,
                taskDeliverables: taskDeliverables,
                isAssignedToTask: isAuthorizedUserParticipant,
            },
        });
    };

    const handleApproveTaskDeliverables = async (path: string) => {
        setApprovingPath(path);

        // Get last slash index
        const lastPathSlashIndex = path.lastIndexOf('/');

        // Get directory name from path
        const directoryName = path.slice(lastPathSlashIndex + 1); // "mycooldeliverable"

        // Get path without directory name
        const directoryPath = path.slice(0, lastPathSlashIndex); // "/directory"

        try {
            await approveDirectory({
                taskDeliverablesTree,
                directoryNameToApprove: directoryName,
                directoryPathToApprove: directoryPath,
            });
        } catch (e) {
            // TODO: handle error
            console.error(e);
        } finally {
            setApprovingPath(null);
        }
    };

    const handleRemoveTaskDeliverable = async (taskDeliverable: TaskDeliverableResource) => {
        setRemovingDeliverableId(taskDeliverable.id);

        try {
            await removeTaskDeliverable({
                path: taskDeliverable.path,
                taskDeliverableId: taskDeliverable.id,
            });
        } catch (e) {
            // TODO: handle error
            console.error(e);
        } finally {
            setRemovingDeliverableId(null);
        }
    };

    const handleApproveTaskDeliverable = async (taskDeliverable: TaskDeliverableResource) => {
        setApprovingDeliverableId(taskDeliverable.id);

        try {
            await approveTaskDeliverable({
                path: taskDeliverable.path,
                taskDeliverableId: taskDeliverable.id,
            });
        } catch (e) {
            // TODO: handle error
            console.error(e);
        } finally {
            setApprovingDeliverableId(null);
        }
    };

    const renderApproveAllButton = (path: string) => {
        if (!isApproveAllowed) {
            return null;
        }

        const isApproving = approvingPath === path;

        return (
            <ConfirmationPopover
                title={`Approve all deliverables at "${path === '/' ? `/${rootTaskDeliverablesPathLabel}` : path}"?`}
                description="When confirmed, client will be able to see deliverables"
                actions={[
                    <Button
                        intent={Intent.SUCCESS}
                        className={Classes.POPOVER_DISMISS}
                        onClick={() => handleApproveTaskDeliverables(path)}
                    >
                        Yes, approve
                    </Button>,
                ]}
            >
                <Button loading={isApproving} disabled={isApproving} className="ml-1" intent={Intent.PRIMARY}>
                    Approve All
                </Button>
            </ConfirmationPopover>
        );
    };

    const renderApproveTaskDeliverableMenuItem = (taskDeliverable: TaskDeliverableResource) => {
        if (!isApproveAllowed) {
            return null;
        }

        const isApprovingDeliverable = approvingDeliverableId === taskDeliverable.id;

        return (
            <Button
                minimal
                disabled={isApprovingDeliverable}
                icon="confirm"
                intent={Intent.SUCCESS}
                onClick={() => handleApproveTaskDeliverable(taskDeliverable)}
            >
                Approve
            </Button>
        );
    };

    const renderRemoveTaskDeliverableMenuItem = (taskDeliverable: TaskDeliverableResource) => {
        if (!isArchiveAllowed) {
            return null;
        }

        const isRemovingDeliverable = removingDeliverableId === taskDeliverable.id;

        return (
            <ConfirmationPopover
                title="Remove deliverable?"
                description="When confirmed, deliverable will be removed"
                actions={[
                    <Button
                        intent={Intent.DANGER}
                        className={Classes.POPOVER_DISMISS}
                        onClick={() => handleRemoveTaskDeliverable(taskDeliverable)}
                    >
                        Yes, remove
                    </Button>,
                ]}
            >
                <Button
                    minimal
                    disabled={isRemovingDeliverable}
                    className="ml-small"
                    icon="trash"
                    intent={Intent.DANGER}
                />
            </ConfirmationPopover>
        );
    };

    const renderDirectoryBlock = (path: string, taskDeliverables: TaskDeliverableResource[]) => {
        // Collect breadcrumbs
        const breadcrumbItems: BreadcrumbProps[] = [];
        breadcrumbItems.push({ text: rootTaskDeliverablesPathLabel });
        if (path !== '/') {
            const parsedPath = path.split('/');
            for (let i = 1; i < parsedPath.length; i++) {
                breadcrumbItems.push({
                    text: parsedPath[i],
                });
            }
        }

        return (
            <div key={path} className="mb-2">
                <Flex className="mb-1" direction="row" align="center" justify="space-between">
                    <Breadcrumbs
                        items={breadcrumbItems}
                        currentBreadcrumbRenderer={({ text, ...restProps }: BreadcrumbProps) => (
                            <Breadcrumb {...restProps}>{text}</Breadcrumb>
                        )}
                    />

                    {renderApproveAllButton(path)}
                </Flex>

                <div>
                    {taskDeliverables
                        .filter((taskDeliverable) => taskDeliverable.approved_at === null)
                        .map((taskDeliverable, index) => (
                            <TaskDeliverableListViewListItem
                                createdAt={taskDeliverable.created_at}
                                approvementTagVisible={false}
                                className={index === 0 ? '' : 'mt-1'}
                                taskDeliverable={taskDeliverable}
                                onOpenViewer={() =>
                                    handleOpenTaskDeliverableViewerModal(taskDeliverable.id, taskDeliverables)
                                }
                                actions={
                                    <>
                                        {renderApproveTaskDeliverableMenuItem(taskDeliverable)}
                                        {renderRemoveTaskDeliverableMenuItem(taskDeliverable)}
                                    </>
                                }
                            />
                        ))}
                </div>
            </div>
        );
    };

    const renderList = () => {
        const directoryBlocks: JSX.Element[] = [];

        for (const path in taskDeliverablesTree.deliverables) {
            // Get task deliverables for path
            const taskDeliverablesForPath = Object.values(taskDeliverablesTree.deliverables[path]);

            // Check if any of the deliverables are not approved for path
            const isPendingApprovalTaskDeliverablesExist = taskDeliverablesForPath.some(
                (taskDeliverable) => taskDeliverable.approved_at === null
            );

            if (isPendingApprovalTaskDeliverablesExist) {
                directoryBlocks.push(renderDirectoryBlock(path, taskDeliverablesForPath));
            }
        }

        if (directoryBlocks.length === 0) {
            // Pending approval task deliverables are NOT found
            return (
                <NonIdealState
                    className="mt-2"
                    icon={<Icon className="mb-2" icon="search" size={70} />}
                    title={
                        <Heading className="mb-1" type="h4">
                            No deliverables to approve
                        </Heading>
                    }
                    action={
                        <Button
                            className="mt-1"
                            intent={Intent.PRIMARY}
                            onClick={() => navigate(Endpoints.TASK_DELIVERABLES_ALL.replace(':taskId', task.id))}
                        >
                            Show all deliverables
                        </Button>
                    }
                />
            );
        }

        return <>{directoryBlocks}</>;
    };

    return <div>{renderList()}</div>;
};

export default TaskDeliverablesApproved;
